import { useContext, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import { companyApiClient } from '../axiosConfig/companyApiClient'
import { COMPANY_PERSONALIZATION_URL } from '../constants/endpoints'
import { PINFLAG_PRIMARY_COLOR } from '../constants/urls'
import { DataContext } from '../context/DataContext'
import { login, getIconData, getLocalStorageKey } from '../helper'

export const useCompanyApi = () => {
  const {
    setCompanyData,
    setCompanyColor,
    setShowCollections,
    setCollections,
    companyDataLoaded,
    setCompanyDataLoaded,
    setLoadingCompanyData
  } = useContext(DataContext)

  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const token = query.get('token') || localStorage.getItem('token') || process.env.REACT_APP_API_KEY

  const handleError = useCallback((companyToken?: string) => {
    const fallback = {
      primaryColor: PINFLAG_PRIMARY_COLOR,
      baseIconPath: 'icons',
      timestamp: '',
      logoUrl: 'https://cms-companies-logos.s3.sa-east-1.amazonaws.com/logo_PinflagTestStore_1688051591793.png'
    }

    const storageKey = getLocalStorageKey(companyToken)
    localStorage.setItem(storageKey, JSON.stringify({ ...fallback, fetchedAt: Date.now() }))

    setCompanyData(fallback)
    setCompanyColor(PINFLAG_PRIMARY_COLOR)
    return { error: true }
  }, [setCompanyData, setCompanyColor])

  const setData = useCallback((data: CompanyData) => {
    setCompanyData(data)
    setCompanyColor(data.primaryColor)
    setShowCollections(data.isActiveCollections)
    setCollections(data.collections)
  }, [setCompanyData, setCompanyColor, setShowCollections, setCollections])

  const fetchCompanyPersonalization = useCallback(async (companyToken?: string) => {
    try {
      login(companyToken)

      const { data } = await companyApiClient.get(`${COMPANY_PERSONALIZATION_URL}`, {
        headers: { 'x-api-key': companyToken ?? '' }
      })

      const { baseIconPath, timestamp } = await getIconData(data)

      const result = {
        baseIconPath,
        timestamp,
        primaryColor: data.primaryColor ?? PINFLAG_PRIMARY_COLOR,
        logoUrl: data.logoUrl,
        companyId: data.companyId,
        isActiveCollections: data.isActiveCollections ?? false,
        collections: data.collections ?? [],
        storePickupDiscountBanner: data.storePickupDiscountBanner ?? null
      }

      const payload = { ...result, fetchedAt: Date.now() }
      localStorage.setItem(getLocalStorageKey(companyToken), JSON.stringify(payload))

      setData(payload)
      return { success: true }
    } catch (error) {
      return handleError(companyToken)
    }
  }, [setData, handleError])

  const fetchData = useCallback(async () => {
    if (companyDataLoaded) return
    setLoadingCompanyData(true)
    await fetchCompanyPersonalization(token)
    setLoadingCompanyData(false)
    setCompanyDataLoaded(true)
  }, [token, fetchCompanyPersonalization, setCompanyDataLoaded, companyDataLoaded, setLoadingCompanyData])

  return {
    fetchCompanyPersonalization,
    fetchData
  }
}

export default useCompanyApi
